import { SpyneTrait } from "spyne";
import { ScrollSpySingleton } from "traits/utils/scroll-spy-singleton";

const _scrollSpy = new ScrollSpySingleton();

// Quiet window after the last scroll event before the spy re-engages, and a
// hard cap so a wedged animation can never leave it disabled permanently.
const SCROLL_SETTLE_MS = 250;
const SCROLL_SETTLE_MAX_MS = 6000;

export class ScrollSpyTraits extends SpyneTrait {
  constructor(context) {
    let traitPrefix = "scrollSpy$";

    super(context, traitPrefix);
  }

  static scrollSpy$ObserverAdd(props = this?.props) {
    const isValidType = this.scrollSpy$ObserverCheckIfValidBlockItem(props);

    const el = props?.el;

    if (isValidType === true && el !== undefined) {
      this.props.isObserved = true;
      _scrollSpy.addSectionObserver(el);
    }
  }

  static scrollSpy$ObserverCheckIfValidBlockItem(
    props = this?.props,
    validItemsArr = ["stage", "container", "section", "topic", "subtopic"],
  ) {
    const itemType = props?.data?.menuData?.itemType;
    const stage = props?.data?.menuData?.stage;

    // Examples children are tab panels selected by click, not sections laid
    // out by scroll position — their geometry overlaps, so observing them
    // lets whichever panel is mounted hijack the Examples stage.
    const isExamplesChild = stage === "examples" && itemType !== "stage";

    return isExamplesChild === false && validItemsArr.includes(itemType);
  }

  static scrollSpy$ObserverRemove(props = this?.props) {
    const isValidType = this.scrollSpy$ObserverCheckIfValidBlockItem(props);

    const el = props?.el;

    if (isValidType === true && el !== undefined) {
      _scrollSpy.removeSectionObserver(el);
    }
  }

  static scrollSpy$ResetDisableScrollSpy(props = this.props) {
    if (props?.disableScrollSpy) {
      props.disableScrollSpy = false;
    }
  }

  // Disables the spy and re-enables it only after window scrolling has been
  // quiet for settleMs. Every scroll event — user, gsap animation, or a
  // late re-scroll from waitForStableHeight — pushes the resume back, so a
  // direct sidebar selection can never be overridden mid-animation by a
  // fixed timer expiring too early. On resume the singleton re-emits the
  // current active section, so route state swallowed during the pause is
  // recovered instead of lost.
  //
  // Pass a longer settleMs for page-load holds: deep-link scrolling starts
  // ~800ms after render (page$OnDeepLink), and a default-length quiet
  // window would resume the spy inside that gap while the window still
  // sits at the top — sending the first section and clobbering the
  // deep-linked route.
  static scrollSpy$PauseScrollSpy(props = this.props, settleMs = SCROLL_SETTLE_MS) {
    if (props === undefined) {
      return;
    }

    props.disableScrollSpy = true;

    // A newer pause supersedes any pending resume.
    this.scrollSpy$ClearPendingResume(props);

    const resume = () => {
      this.scrollSpy$ClearPendingResume(props);
      if (props !== undefined) {
        props.disableScrollSpy = false;
        _scrollSpy.emitActive();
      }
    };

    const onScroll = () => {
      clearTimeout(props._scrollSpySettleId);
      // Reset with this pause's own quiet window, not the default —
      // callers that arm a longer span rely on it surviving scroll events.
      props._scrollSpySettleId = setTimeout(resume, settleMs);
    };

    props._scrollSpyOnScroll = onScroll;
    window.addEventListener("scroll", onScroll, { passive: true });

    // Arm immediately: if nothing ends up scrolling, the spy resumes after
    // one quiet window instead of staying dark.
    props._scrollSpySettleId = setTimeout(resume, settleMs);
    props._scrollSpyMaxId = setTimeout(resume, SCROLL_SETTLE_MAX_MS);
  }

  static scrollSpy$ClearPendingResume(props = this.props) {
    if (props === undefined) {
      return;
    }
    clearTimeout(props._scrollSpySettleId);
    clearTimeout(props._scrollSpyMaxId);
    if (props._scrollSpyOnScroll) {
      window.removeEventListener("scroll", props._scrollSpyOnScroll);
      props._scrollSpyOnScroll = undefined;
    }
  }

  static scrollSpy$SubscribeAndSendToRoute(props = this.props) {
    const onSubscribeToIntersections = (dataArr) => {
      if (this?.props?.disableScrollSpy === false) {
        // Route-level keys only: element datasets also carry vsid/itemId
        // and similar flags that must not leak into the route payload.
        // Absent levels are sent as explicit "" (matching what sidebar
        // links send) so a stage-level emission CLEARS deeper route state
        // — including a reference classMethod — instead of inheriting it.
        const routeLevels = [
          "stage",
          "container",
          "section",
          "topic",
          "subtopic",
          "classMethod",
        ];
        const dataset = dataArr[0].target.dataset;
        const payload = { pageId: dataset.pageId };
        routeLevels.forEach((key) => {
          payload[key] = dataset[key] ?? "";
        });

        // No-op guard: if the current URL already begins with the
        // segments this payload projects, sending would change nothing —
        // but the route channel would still push a duplicate history
        // entry, leaving the back button apparently dead (each press pops
        // an identical URL). Deeper current segments (a selected example
        // or class method) are deliberately preserved by skipping.
        const projected = [
          payload.pageId,
          payload.stage,
          payload.container,
          payload.section,
          payload.topic,
          payload.subtopic,
        ].filter((s) => s !== undefined && s !== "");
        const currentSegments = window.location.pathname
          .split("/")
          .filter(Boolean);
        const isNoOp = projected.every(
          (seg, i) => currentSegments[i] === seg,
        );

        if (isNoOp === true) {
          return;
        }

        // Inside Examples the tab panels own the deeper route (they're
        // click-selected and unobserved), so the spy only knows the stage.
        // Re-publishing the stage while already within /docs/examples
        // would strip the selected example's container — stay silent.
        // Entering Examples from another stage still sends (with "" keys
        // clearing the previous stage's deeper state).
        const isWithinExamples =
          payload.stage === "examples" &&
          window.location.pathname.startsWith("/docs/examples");

        if (isWithinExamples === false) {
          this.sendInfoToChannel("CHANNEL_ROUTE", payload);
        }
      }
    };

    const { intersections$ } = _scrollSpy;

    props._scrollSpySub = intersections$.subscribe(onSubscribeToIntersections);
  }

  // Pages call this on dispose so stale subscriptions (and their retained
  // ViewStream contexts) don't pile up across route changes.
  static scrollSpy$Unsubscribe(props = this.props) {
    this.scrollSpy$ClearPendingResume(props);
    if (props?._scrollSpySub) {
      props._scrollSpySub.unsubscribe();
      props._scrollSpySub = undefined;
    }
  }
}
